// 在最早时机抑制 ResizeObserver 循环错误（这是一个已知的浏览器警告，不影响功能）
const resizeObserverLoopErrRe = /^[^(]*ResizeObserver loop[^)]*$/;
const resizeObserverLoopErrRe2 = /^ResizeObserver loop completed with undelivered notifications[^)]*$/;

// 捕获全局错误（在应用启动前）
window.addEventListener('error', (event) => {
  if (
    event.message &&
    (resizeObserverLoopErrRe.test(event.message) || resizeObserverLoopErrRe2.test(event.message))
  ) {
    event.preventDefault();
    event.stopPropagation();
    return false;
  }
}, true); // 使用捕获阶段

// 捕获未处理的 Promise 拒绝
window.addEventListener('unhandledrejection', (event) => {
  if (
    event.reason &&
    typeof event.reason === 'string' &&
    (resizeObserverLoopErrRe.test(event.reason) || resizeObserverLoopErrRe2.test(event.reason))
  ) {
    event.preventDefault();
    event.stopPropagation();
    return false;
  }
});

// 重写 console.error（在应用启动前）
const originalError = window.console.error;
window.console.error = (...args) => {
  if (
    args.length > 0 &&
    typeof args[0] === 'string' &&
    (resizeObserverLoopErrRe.test(args[0]) || resizeObserverLoopErrRe2.test(args[0]))
  ) {
    return;
  }
  originalError.apply(console, args);
};

import { createApp, nextTick } from 'vue';
import { createPinia } from 'pinia';
import ElementPlus from 'element-plus';
import 'element-plus/theme-chalk/index.css';
import zhCn from 'element-plus/es/locale/lang/zh-cn';
import { scheduleWebfontLoad } from './loadWebfonts.js';
import { registerServiceWorker } from './registerServiceWorker.js';
import App from './App.vue';

/** 与设计稿分页文案一致（EP zh-cn 默认 goto 为「前往」） */
const zhCnDesign = {
  ...zhCn,
  el: {
    ...zhCn.el,
    pagination: {
      ...zhCn.el.pagination,
      goto: '跳至'
    }
  }
};
import router from './router';
import '@styles/index.scss';
/** D2C 工作台壳层：须在全局样式之后加载，才能覆盖 EP 按钮默认底/hover（否则异步 chunk 里样式顺序不稳定） */
import './styles/d2c-workbench-shell.scss';
import './styles/dialog-card-global.scss';
/** 基础组件文档页：全局生效，供 DsShowcaseVariant / 子组件演示块使用 */
import './styles/ds-showcase-doc.global.scss';
import './page-layouts/_shared/wireframe.global.scss';
import { applySymbolOverrides, loadIconfont } from './utils/iconfont';
import { vPmDocPinnedScrollEdge } from './directives/pmDocPinnedScrollEdge.js';

const app = createApp(App);
app.directive('pm-doc-pinned-scroll-edge', vPmDocPinnedScrollEdge);
const pinia = createPinia();

// 全局加载 iconfont（业务组件图标统一来源；有 override 时用 public 内 patched 脚本）
loadIconfont();
applySymbolOverrides();

/** HarmonyOS + Montserrat：异步 chunk，不阻塞首屏 CSS/JS */
scheduleWebfontLoad();

/** 静态资源（含字体 woff2）本地 Cache Storage 留存，二次访问加速 */
registerServiceWorker();

// 注册 Element Plus，并设置中文
app.use(ElementPlus, {
  locale: zhCnDesign
});
app.use(pinia);
app.use(router);

// 业务与预览页图标统一使用 iconfont + PmIcon（见 design-rules/icons.md）；勿全局注册 @element-plus/icons-vue（界面禁用 EP 图标组件，见 design-rules/element-plus-integration.md）

app.mount('#app');

// 表头 tooltip：统一走 Element Plus（show-header-overflow-tooltip / PmTableHeadLabel），
// 禁止原生 title（浏览器默认浅色气泡，与设计规范深色 tooltip 不一致）。
const stripNativeTableHeaderTitles = () => {
  document.querySelectorAll('th.el-table__cell .cell[title]').forEach((cell) => {
    cell.removeAttribute('title');
  });
};

nextTick(() => {
  setTimeout(stripNativeTableHeaderTitles, 300);
});

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', stripNativeTableHeaderTitles);
} else {
  stripNativeTableHeaderTitles();
}

const tableHeaderTitleObserver = new MutationObserver(stripNativeTableHeaderTitles);
tableHeaderTitleObserver.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['title'] });
window.addEventListener('resize', stripNativeTableHeaderTitles);
